import {
  BriefDeliverableStatusEnum,
  UploaderType
} from "@entities/brief/lib/constants";
import {
  IBriefCampaignInfluencerSearchRequestV2,
} from "@shared/lib/types/briefCampains";
import {
  IBriefDeliverableConfig
} from "@shared/lib/types/briefConfig";
import { SearchMultiSelectFilter } from "@shared/ui/filters/SearchMultiSelectFilter";
import { Flex } from "antd";
import { ColumnType } from "antd/es/table";
import React from "react";
import {
  CampaignExecutionTableRow,
  findTaskForStage,
  OpenTaskDrawerParams,
  STAGE_COLUMNS,
} from "../../lib";
import { DeliverableStageCell } from "./DeliverableCells";

export { findTaskForStage, STAGE_COLUMNS };

interface BuildStageColumnsParams {
  selectedDeliverableId: string | null;
  config?: IBriefDeliverableConfig[];
  facets: {
    _id: string;
    completedCount: number;
    count: number;
  }[];
  filters?: IBriefCampaignInfluencerSearchRequestV2;
  setFilters?: React.Dispatch<
    React.SetStateAction<IBriefCampaignInfluencerSearchRequestV2>
  >;
  onOpenTaskDrawer?: (params: OpenTaskDrawerParams) => void;
}

export const buildStageColumns = ({
  selectedDeliverableId,
  config,
  facets,
  filters,
  setFilters,
  onOpenTaskDrawer,
}: BuildStageColumnsParams): ColumnType<CampaignExecutionTableRow>[] => {
  return STAGE_COLUMNS.map((stage) => {
    const selectedDeliverableConfig = selectedDeliverableId
      ? config?.find((c) => c.deliverableConfigRefId === selectedDeliverableId)
      : undefined;

    const stageTask = selectedDeliverableConfig
      ? findTaskForStage(
          selectedDeliverableConfig.tasks,
          stage.key,
          stage.label
        )
      : undefined;

    const facet = stageTask
      ? facets.find((f) => f._id === stageTask.taskId)
      : undefined;

    const taskFilter =
      selectedDeliverableId && stageTask
        ? filters?.deliverables?.[selectedDeliverableId]?.tasks?.[
            stageTask.taskId
          ]
        : undefined;

    const filterCount =
      (taskFilter?.statuses?.length || 0) +
      ((taskFilter?.approvalPendingOn?.length || 0) > 0 ? 1 : 0);

    const isFilterActive = filterCount > 0;

    return {
      title: (
        <Flex
          align="center"
          justify="center"
          gap={4}
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase" as const,
            color: "#9ba3b2",
            letterSpacing: "0.05em",
          }}
        >
          <span>{stage.label}</span>
          {facet && (
            <span
              style={{
                fontWeight: 500,
                textTransform: "none",
                fontSize: 10,
              }}
            >
              ({facet.completedCount}/{facet.count})
            </span>
          )}
        </Flex>
      ),
      dataIndex: `stage_${stage.key}`,
      key: `stage_${stage.key}`,
      width: 140,
      align: "center" as const,
      onHeaderCell: () => ({
        style: {
          background: "var(--bg-primary)",
          transition: "background-color 0.3s ease",
        },
      }),
      ...(selectedDeliverableId && stageTask
        ? {
            filterCount,
            filtered: isFilterActive,
            filterDropdownProps: {
              destroyPopupOnHide: true,
            },
            filterDropdown: (props: {
              confirm: () => void;
              close: () => void;
            }) => {
              if (!setFilters) return null;
              return (
                <SearchMultiSelectFilter
                  confirm={props.confirm}
                  close={props.close}
                  searchPlaceholder={`Search ${stage.label} Status`}
                  options={[
                    ...Object.values(BriefDeliverableStatusEnum)
                      .filter(
                        (item) =>
                          ![
                            BriefDeliverableStatusEnum.Uploaded,
                            BriefDeliverableStatusEnum.Approved,
                            BriefDeliverableStatusEnum.PendingApproval,
                          ].includes(item as BriefDeliverableStatusEnum)
                      )
                      .map((item) => ({
                        label: item,
                        value: item,
                      })),
                    {
                      label: "Approval Pending",
                      value: "Approval-Pending",
                    },
                  ]}
                  selectedValues={[
                    ...(taskFilter?.statuses || []),
                    ...((taskFilter?.approvalPendingOn?.length || 0) > 0
                      ? ["Approval-Pending"]
                      : []),
                  ]}
                  onApply={(value) => {
                    const isApprovalPendingSelected =
                      value.includes("Approval-Pending");
                    const approvalItems = isApprovalPendingSelected
                      ? Object.values(UploaderType)
                      : [];
                    const statusItems = value.filter(
                      (item) => item !== "Approval-Pending"
                    );

                    setFilters((prev) => ({
                      ...prev,
                      deliverables: {
                        ...prev.deliverables,
                        [selectedDeliverableId]: {
                          ...prev.deliverables?.[selectedDeliverableId],
                          tasks: {
                            ...prev.deliverables?.[selectedDeliverableId]?.tasks,
                            [stageTask.taskId]: {
                              statuses:
                                statusItems as BriefDeliverableStatusEnum[],
                              approvalPendingOn:
                                approvalItems as UploaderType[],
                            },
                          },
                        },
                      },
                    }));
                  }}
                  onReset={() => {
                    setFilters((prev) => {
                      const prevDeliverables = { ...prev.deliverables };
                      if (prevDeliverables[selectedDeliverableId]?.tasks) {
                        const updatedTasks = {
                          ...prevDeliverables[selectedDeliverableId].tasks,
                        };
                        delete updatedTasks[stageTask.taskId];
                        prevDeliverables[selectedDeliverableId] = {
                          ...prevDeliverables[selectedDeliverableId],
                          tasks: updatedTasks,
                        };
                      }
                      return {
                        ...prev,
                        deliverables: prevDeliverables,
                      };
                    });
                  }}
                />
              );
            },
          }
        : {}),
      render: (_: unknown, record: CampaignExecutionTableRow) => {
        if (record.deliverableItem) {
          return (
            <DeliverableStageCell
              item={record.deliverableItem}
              stageKey={stage.key}
              stageLabel={stage.label}
              onOpenTaskDrawer={onOpenTaskDrawer}
            />
          );
        }
        return record.children ? "" : "-";
      },
    };
  });
};
